import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '@core/services/auth/auth.service';
import { LocalStorageService } from '@core/services/local-storage/local-storage.service';
import { EmailErrorMessage, ProvinceModel, UserErrorMessage, UserModel } from '@core/services/user/user.model';
import { UserService } from '@core/services/user/user.service';
import { emailDomainValidator } from '@shared/directives/email-domain.directive';
import { emailFormatValidator } from '@shared/directives/email-format.directive';
import { phoneExtensionFormatValidator, phoneFormatValidator } from '@shared/directives/phone-format.directive';
import { NgxSpinnerService } from 'ngx-spinner';
import { inputNoWhitespaceValidator } from '@shared/directives/input-no-whitespace.directive';

const PAGE_TITLE: string = 'Update User Profile';

@Component({
  selector: 'Teranet-UserProfile',
  templateUrl: './user-profile.component.html'
})
export class UserProfileComponent implements OnInit {  
  emailErrorMessage = EmailErrorMessage;
  errorMessages: string[] = [];
  hasError: boolean = false;
  languages: string[] = ['English', 'French'];
  pageTitle: string = PAGE_TITLE;
  previousPage: string = '';
  provinces: ProvinceModel[] = [];
  userErrorMessage = UserErrorMessage;
  userModel: UserModel;
  userProfileForm: FormGroup;

  @ViewChild('email', { static: false }) emailRef: ElementRef;
  @ViewChild('ext', { static: false }) extRef: ElementRef;
  @ViewChild('firstName', { static: false }) firstNameRef: ElementRef;
  @ViewChild('language', { static: false }) languageRef: ElementRef;
  @ViewChild('lastName', { static: false }) lastNameRef: ElementRef;
  @ViewChild('middleName', { static: false }) middleNameRef: ElementRef;
  @ViewChild('phone', { static: false }) phoneRef: ElementRef;
  @ViewChild('provinceId', { static: false }) provinceIdRef: ElementRef;

  constructor(
    private authService: AuthService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private userService: UserService,
  ) {
    this.userProfileForm = new FormGroup({
      email: new FormControl('', [emailFormatValidator, emailDomainValidator]),
      ext: new FormControl('', phoneExtensionFormatValidator),
      firstName: new FormControl('', [inputNoWhitespaceValidator, Validators.required, Validators.maxLength(100)]),
      language: new FormControl('', Validators.required),
      lastName: new FormControl('', [inputNoWhitespaceValidator, Validators.required, Validators.maxLength(140)]),
      middleName: new FormControl('', [inputNoWhitespaceValidator, Validators.maxLength(100)]),
      phone: new FormControl('', phoneFormatValidator),
      provinceId: new FormControl('', Validators.required),
    })
  }

  ngOnInit() {
    this.spinner.show();

    this.userService.getProvinces().subscribe(value => {
      this.provinces = value as ProvinceModel[];
    });

    this.getUserProfile();
  }

  // Check errors upon submission and show all the errors on top
  checkError() {
    this.hasError = false;
    this.errorMessages = [];
    let firstControl: ElementRef<any> = null;

    if (this.userProfileForm.get('firstName') !== null) {
      if (this.userProfileForm.get('firstName').value.trim() === '') {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.firstNameRef;
        }
        this.userProfileForm.get('firstName').markAsTouched();
        this.errorMessages.push(UserErrorMessage.FIRST_NAME_REQUIRED);
      } else if (this.userProfileForm.get('firstName').hasError('maxlength')) {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.firstNameRef;
        }
        this.userProfileForm.get('firstName').markAsTouched();
        this.errorMessages.push(`${UserErrorMessage.FIRST_NAME_MAX_LENGTH}${this.userProfileForm.get('firstName').errors.maxlength.requiredLength}`);
      }
    }

    if (this.userProfileForm.get('middleName') !== null) {
      if (this.userProfileForm.get('middleName').hasError('whitespace')) {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.middleNameRef;
        }
        this.userProfileForm.get('middleName').markAsTouched();
        this.errorMessages.push(UserErrorMessage.MIDDLE_NAME_WHITESPACE);
      } else if (this.userProfileForm.get('middleName').hasError('maxlength')) {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.middleNameRef;
        }
        this.userProfileForm.get('middleName').markAsTouched();
        this.errorMessages.push(`${UserErrorMessage.MIDDLE_NAME_MAX_LENGTH}${this.userProfileForm.get('middleName').errors.maxlength.requiredLength}`);
      }
    }

    if (this.userProfileForm.get('lastName') !== null) {
      if (this.userProfileForm.get('lastName').value.trim() === '') {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.lastNameRef;
        }
        this.userProfileForm.get('lastName').markAsTouched();
        this.errorMessages.push(UserErrorMessage.LAST_NAME_REQUIRED);
      } else if (this.userProfileForm.get('lastName').hasError('maxlength')) {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.lastNameRef;
        }
        this.userProfileForm.get('lastName').markAsTouched();
        this.errorMessages.push(`${UserErrorMessage.LAST_NAME_MAX_LENGTH}${this.userProfileForm.get('lastName').errors.maxlength.requiredLength}`);
      }
    }

    if (this.userProfileForm.get('email') !== null) {
      if (this.userProfileForm.get('email').value !== '') {
        if (this.userProfileForm.get('email').hasError('invalidEmail')) {
          this.hasError = true;
          if (firstControl === null) {
            firstControl = this.emailRef;
          }
          this.userProfileForm.get('email').markAsTouched();
          this.errorMessages.push(EmailErrorMessage.IS_NOT_EMAIL);
        } else if (this.userProfileForm.get('email').hasError('invalidDomain')) {
          this.hasError = true;
          if (firstControl === null) {
            firstControl = this.emailRef;
          }
          this.userProfileForm.get('email').markAsTouched();
          this.errorMessages.push(EmailErrorMessage.EMAIL_DOMAIN);
        }
      }
    }

    if (this.userProfileForm.get('phone') !== null) {
      if (this.userProfileForm.get('phone').value !== '') {
        if (this.userProfileForm.get('phone').hasError('invalidPhone')) {
          this.hasError = true;
          if (firstControl === null) {
            firstControl = this.phoneRef;
          }
          this.userProfileForm.get('phone').markAsTouched();
          this.errorMessages.push(UserErrorMessage.IS_NOT_PHONE_NUMBER);
        }
      }
    }

    if (this.userProfileForm.get('ext') !== null) {
      if (this.userProfileForm.get('ext').value !== '') {
        if (this.userProfileForm.get('ext').hasError('invalidExtension')) {
          this.hasError = true;
          if (firstControl === null) {
            firstControl = this.extRef;
          }
          this.userProfileForm.get('ext').markAsTouched();
          this.errorMessages.push(UserErrorMessage.IS_NOT_PHONE_EXTENSION);
        }
      }
    }

    if (this.userProfileForm.get('language') !== null) {
      if (
        this.userProfileForm.get('language').value === null ||
        this.userProfileForm.get('language').value === ''
      ) {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.languageRef;
        }
        this.userProfileForm.get('language').markAsTouched();
        this.errorMessages.push(UserErrorMessage.LANGUAGE_REQUIRED);
      }
    }

    if (this.userProfileForm.get('provinceId') !== null) {
      if (
        this.userProfileForm.get('provinceId').value === null ||
        this.userProfileForm.get('provinceId').value === ''
      ) {
        this.hasError = true;
        if (firstControl === null) {
          firstControl = this.provinceIdRef;
        }
        this.userProfileForm.get('provinceId').markAsTouched();
        this.errorMessages.push(UserErrorMessage.PROVINCE_REQUIRED);
      }
    }

    // Focus the Error Div
    if (this.hasError) {
      if (firstControl !== null && firstControl.nativeElement !== null) {
        firstControl.nativeElement.focus();
      }
    }
  }

  getUserProfile() {
    this.userService.getUserProfile().subscribe(value => {
      if (value) {
        this.userModel = value as UserModel;
        this.setUserProfile();
        this.spinner.hide();
      }
      else {
        this.spinner.hide();
        this.goBack();
      }
    });
  }

  goBack(): void {
    this.router.navigate(['dashboard']);
  }

  setUserProfile() {
    if (this.userModel != null) {
      // Set form values
      if (this.userModel.firstName && this.userModel.firstName.length > 0) {
        this.userProfileForm.get('firstName').setValue(this.userModel.firstName);
      }

      if (this.userModel.middleName && this.userModel.middleName.length > 0) {
        this.userProfileForm.get('middleName').setValue(this.userModel.middleName);
      }

      if (this.userModel.lastName && this.userModel.lastName.length > 0) {
        this.userProfileForm.get('lastName').setValue(this.userModel.lastName);
      }

      if (this.userModel.email && this.userModel.email.length > 0) {
        this.userProfileForm.get('email').setValue(this.userModel.email);
      }

      if (this.userModel.phone && this.userModel.phone.length > 0 && this.userModel.phone.indexOf('.') !== -1) {
        this.userProfileForm.get('phone').setValue(this.userModel.phone.split('.')[0] !== null ? this.userModel.phone.split('.')[0].trim() : '');
        this.userProfileForm.get('ext').setValue(this.userModel.phone.split('.')[1] !== null ? this.userModel.phone.split('.')[1].trim() : '');
      }

      if (this.userModel.language && this.userModel.language.length > 0) {
        this.userProfileForm.get('language').setValue(this.userModel.language);
      }

      if (this.userModel.provinceId > 0) {
        this.userProfileForm.get('provinceId').setValue(this.userModel.provinceId);
      }
    }
  }

  updateUserProfile() {
    this.spinner.show();
    this.checkError();

    if (this.userProfileForm.valid && !this.hasError) {
      const updatedUserProfile: UserModel = {
        email: this.userProfileForm.get('email').value.trim(),
        firstName: this.userProfileForm.get('firstName').value.trim(),
        id: this.userModel.id,
        language: this.userProfileForm.get('language').value,
        lastName: this.userProfileForm.get('lastName').value.trim(),
        middleName: this.userProfileForm.get('middleName').value?.trim(),
        phone: this.userProfileForm.get('phone').value?.trim() + '.' + this.userProfileForm.get('ext').value?.trim(),
        provinceId: this.userProfileForm.get('provinceId').value,
      };
      let currentUser = this.localStorageService.getItem("currentUser");
      currentUser.email = updatedUserProfile.email;
      currentUser.username = updatedUserProfile.firstName + ' ' + updatedUserProfile.lastName;
      // Update User Profile
      this.userService.updateUserProfile(updatedUserProfile).subscribe(() => {
        this.localStorageService.setItem('currentUser', currentUser);
        this.authService.currentUserSubject.next(currentUser);
        this.spinner.hide();
        this.goBack();
      })
    }

    this.spinner.hide();
  }
}
